import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Youtube, ExternalLink, Image as ImageIcon } from 'lucide-react';
import MediaUploader from '@/components/admin/MediaUploader';

/**
 * VideoListEditor — edit an array of { url, thumbnail_url, caption } objects.
 *
 * Props:
 * - value: [{ url, thumbnail_url, caption }]
 * - onChange: (nextArray) => void
 * - testId?: string
 */

// Auto-convert YouTube / Vimeo watch URLs into embed URLs.
export const normaliseEmbedUrl = (raw) => {
    if (!raw) return '';
    const url = String(raw).trim();
    // YouTube watch URL
    const yt1 = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
    if (yt1) return `https://www.youtube.com/embed/${yt1[1]}`;
    // YouTube shorts
    const yt2 = url.match(/youtube\.com\/shorts\/([\w-]{11})/);
    if (yt2) return `https://www.youtube.com/embed/${yt2[1]}`;
    // Vimeo (numeric ID)
    const vim = url.match(/vimeo\.com\/(\d+)/);
    if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
    return url;
};

export const VideoListEditor = ({ value = [], onChange, testId, prefix = 'gallery/video-thumbs' }) => {
    const items = Array.isArray(value) ? value : [];
    const [openIdx, setOpenIdx] = useState(null);

    const update = (idx, patch) => {
        const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
        onChange && onChange(next);
    };
    const add = () =>
        onChange && onChange([...items, { url: '', thumbnail_url: '', caption: '' }]);
    const remove = (idx) =>
        onChange && onChange(items.filter((_, i) => i !== idx));

    return (
        <div className="space-y-3" data-testid={testId}>
            {items.length > 0 && (
                <div className="space-y-2">
                    {items.map((it, idx) => (
                        <Card
                            key={idx}
                            className="bg-card border-border overflow-hidden"
                            data-testid={testId ? `${testId}-row-${idx}` : undefined}
                        >
                            <CardContent className="p-3">
                                <div className="flex items-center gap-2">
                                    <Youtube className="h-4 w-4 text-[hsl(var(--primary))] shrink-0" />
                                    <Input
                                        value={it.url || ''}
                                        onChange={(e) =>
                                            update(idx, { url: e.target.value })
                                        }
                                        onBlur={(e) => {
                                            const cleaned = normaliseEmbedUrl(e.target.value);
                                            if (cleaned && cleaned !== e.target.value) {
                                                update(idx, { url: cleaned });
                                            }
                                        }}
                                        placeholder="YouTube or Vimeo URL (watch or embed link)"
                                        className="flex-1"
                                        data-testid={testId ? `${testId}-url-${idx}` : undefined}
                                    />
                                    {it.url && (
                                        <a
                                            href={it.url}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            className="h-8 w-8 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--secondary))]"
                                            aria-label="Open video"
                                            title="Open in new tab"
                                        >
                                            <ExternalLink className="h-3.5 w-3.5" />
                                        </a>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                                        title="More options (thumbnail, caption)"
                                        className="h-8 w-8 shrink-0"
                                    >
                                        <ImageIcon className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => remove(idx)}
                                        className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                                        data-testid={testId ? `${testId}-remove-${idx}` : undefined}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                                {openIdx === idx && (
                                    <div className="mt-3 grid gap-3 border-t border-border pt-3">
                                        <div>
                                            <div className="text-xs text-muted-foreground mb-1.5">
                                                Thumbnail (used as preview)
                                            </div>
                                            <MediaUploader
                                                value={it.thumbnail_url || ''}
                                                onChange={(v) => update(idx, { thumbnail_url: v })}
                                                kind="image"
                                                prefix={prefix}
                                                testId={testId ? `${testId}-thumb-${idx}` : undefined}
                                                allowUrlPaste={true}
                                            />
                                        </div>
                                        <div>
                                            <div className="text-xs text-muted-foreground mb-1.5">
                                                Caption (optional)
                                            </div>
                                            <Input
                                                value={it.caption || ''}
                                                onChange={(e) =>
                                                    update(idx, { caption: e.target.value })
                                                }
                                                placeholder="Short caption"
                                                data-testid={testId ? `${testId}-caption-${idx}` : undefined}
                                            />
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={add}
                data-testid={testId ? `${testId}-add` : undefined}
            >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add video
            </Button>
            <div className="text-xs text-muted-foreground">
                Paste a YouTube or Vimeo URL. Watch links (e.g. <code>youtube.com/watch?v=...</code>) are auto-converted to embed format.
            </div>
        </div>
    );
};

export default VideoListEditor;
